import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Slug } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { full_name: "", company: "", email: "", phone: "", inquiry_type: "option", message: "" };
const TYPES = [["option", "Option agreement"], ["purchase", "Outright purchase"], ["shopping", "Shopping agreement"], ["script_request", "Request the script"], ["other", "Other"]];
const field = "h-12 rounded-none border-0 border-b border-white/20 bg-transparent px-0 text-base text-[#E2E0D8] placeholder:text-[#8A93A3] focus-visible:border-[#C81D25] focus-visible:ring-0";

export const InquiryForm = () => {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { ...f, company: f.company || null, phone: f.phone || null });
      toast.success("Inquiry received. Nicola will be in touch.");
      setF(empty);
    } catch (err) {
      toast.error("Could not send. Please check your details or email directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Reveal className="border border-white/10 bg-[#0E1017]/85 p-6 backdrop-blur-md md:p-10">
      <form onSubmit={submit} data-testid="inquiry-form" className="space-y-8">
        <Slug>Send an inquiry</Slug>
        <div className="grid gap-8 md:grid-cols-2">
          <Input required placeholder="Full name *" value={f.full_name} onChange={set("full_name")} className={field} data-testid="inquiry-input-name" />
          <Input placeholder="Company / agency" value={f.company} onChange={set("company")} className={field} data-testid="inquiry-input-company" />
          <Input required type="email" placeholder="Email *" value={f.email} onChange={set("email")} className={field} data-testid="inquiry-input-email" />
          <Input placeholder="Phone" value={f.phone} onChange={set("phone")} className={field} data-testid="inquiry-input-phone" />
        </div>
        <Select value={f.inquiry_type} onValueChange={(v) => setF({ ...f, inquiry_type: v })}>
          <SelectTrigger className={field} data-testid="inquiry-select-type"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none border-white/10 bg-[#0E1017] text-[#E2E0D8]">
            {TYPES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`inquiry-type-${v}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea required rows={5} placeholder="Tell us about your project *" value={f.message} onChange={set("message")} className={`${field} min-h-[140px] resize-none py-3`} data-testid="inquiry-textarea-message" />
        <button disabled={busy} type="submit" data-testid="inquiry-submit-btn" className="flex items-center gap-3 rounded-full bg-[#C81D25] px-8 py-4 font-mono-s text-xs uppercase tracking-[0.25em] text-[#E2E0D8] transition-colors duration-300 hover:bg-[#E76F51] disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />} Submit inquiry
        </button>
      </form>
    </Reveal>
  );
};
